import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material'
import type { PoliticaSpreadRequest } from '@/types/simulacaoComissao'
import type { Parceiro, Produto } from '@/types/produto'
import { lerPercentualPtBr, percentualParaFracao } from '../fracao'
import { errosDaFalha, HELPER_ORIGEM } from './errosFormulario'
import { inicioPadraoProposta } from './inicioPadrao'

interface Props {
  aberto: boolean
  parceiros: Parceiro[]
  produtos: Produto[]
  onFechar: () => void
  onSalvar: (request: PoliticaSpreadRequest) => Promise<void>
}

/**
 * Nova versão de spread parceiro × produto. Parceiro e produto vêm de listas; o spread é
 * digitado em PERCENTUAL (vírgula) e vai para a API como FRAÇÃO — a única conversão da
 * tela. Sem valor padrão: spread é dado do Financeiro, nunca sugerido pelo código.
 */
export const NovoSpreadDialog: FC<Props> = ({ aberto, parceiros, produtos, onFechar, onSalvar }) => {
  const [parceiroId, setParceiroId] = useState('')
  const [produtoId, setProdutoId] = useState('')
  const [spread, setSpread] = useState('')
  const [vigenciaInicio, setVigenciaInicio] = useState(() => inicioPadraoProposta())
  const [vigenciaFim, setVigenciaFim] = useState('')
  const [origem, setOrigem] = useState('')
  const [erros, setErros] = useState<Record<string, string>>({})
  const [erroGeral, setErroGeral] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    if (aberto) {
      setParceiroId('')
      setProdutoId('')
      setSpread('')
      setVigenciaInicio(inicioPadraoProposta())
      setVigenciaFim('')
      setOrigem('')
      setErros({})
      setErroGeral(null)
    }
  }, [aberto])

  const salvar = async () => {
    const faltando: Record<string, string> = {}
    if (!parceiroId) faltando.parceiroId = 'Escolha o parceiro'
    if (!produtoId) faltando.produtoId = 'Escolha o produto'
    const spreadPercentual = lerPercentualPtBr(spread)
    if (spreadPercentual == null) faltando.spread = 'Informe o spread em percentual, com vírgula (ex.: 2,5)'
    if (!vigenciaInicio) faltando.vigenciaInicio = 'Informe o início da vigência'
    if (!origem.trim()) faltando.origem = 'Informe a origem (e-mail, ata ou contrato)'
    if (Object.keys(faltando).length) {
      setErros(faltando)
      return
    }
    const parceiro = parceiros.find((p) => p.id === parceiroId)
    setSalvando(true)
    setErros({})
    setErroGeral(null)
    try {
      await onSalvar({
        parceiroId,
        parceiroNome: parceiro?.nome ?? null,
        produtoId,
        spread: percentualParaFracao(spreadPercentual as number),
        vigenciaInicio,
        vigenciaFim: vigenciaFim || null,
        origem: origem.trim(),
      })
      onFechar()
    } catch (e) {
      const r = errosDaFalha(e)
      setErros(r.porCampo)
      setErroGeral(r.geral)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Dialog open={aberto} onClose={salvando ? undefined : onFechar} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Propor versão — spread por parceiro × produto</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {erroGeral && <Alert severity="error">{erroGeral}</Alert>}
          <TextField
            select
            SelectProps={{ native: true }}
            label="Parceiro"
            value={parceiroId}
            onChange={(e) => setParceiroId(e.target.value)}
            error={!!erros.parceiroId}
            helperText={erros.parceiroId ?? 'Parceiros ativos do cadastro'}
            InputLabelProps={{ shrink: true }}
            fullWidth
          >
            <option value="">Selecione…</option>
            {parceiros.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome}
              </option>
            ))}
          </TextField>
          <TextField
            select
            SelectProps={{ native: true }}
            label="Produto"
            value={produtoId}
            onChange={(e) => setProdutoId(e.target.value)}
            error={!!erros.produtoId}
            helperText={erros.produtoId ?? 'Do catálogo de produtos'}
            InputLabelProps={{ shrink: true }}
            fullWidth
          >
            <option value="">Selecione…</option>
            {produtos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome}
              </option>
            ))}
          </TextField>
          <TextField
            label="Spread"
            value={spread}
            onChange={(e) => setSpread(e.target.value)}
            error={!!erros.spread}
            helperText={erros.spread ?? 'Percentual com vírgula, ex.: 2,5 (a API guarda como fração)'}
            inputProps={{ inputMode: 'decimal' }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            required
            fullWidth
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Início da vigência"
              type="date"
              value={vigenciaInicio}
              onChange={(e) => setVigenciaInicio(e.target.value)}
              error={!!erros.vigenciaInicio}
              helperText={erros.vigenciaInicio}
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
            />
            <TextField
              label="Fim da vigência (opcional)"
              type="date"
              value={vigenciaFim}
              onChange={(e) => setVigenciaFim(e.target.value)}
              error={!!erros.vigenciaFim}
              helperText={erros.vigenciaFim ?? 'Vigência semiaberta: vale até o dia anterior'}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
          <TextField
            label="Origem"
            value={origem}
            onChange={(e) => setOrigem(e.target.value)}
            error={!!erros.origem}
            helperText={erros.origem ?? HELPER_ORIGEM}
            inputProps={{ maxLength: 200 }}
            required
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onFechar} disabled={salvando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void salvar()}
          disabled={salvando}
          startIcon={salvando ? <CircularProgress size={18} /> : undefined}
        >
          Enviar para aprovação
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default NovoSpreadDialog
